import { Location } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { NavigationHistoryService } from 'src/app/shared/services/navigation-history.service';

export type BackButtonVariant = 'outline' | 'ghost' | 'icon';
export type BackButtonSize = 'sm' | 'md';

/**
 * Reusable back button.
 *
 * Behavior, in order:
 *   1. If `link` is set, route there (forces a destination, ignores history).
 *   2. Else if we have in-app history (navigated at least once this session),
 *      pop the browser history.
 *   3. Else if `fallbackLink` is set, route there.
 *   4. Else navigate to `/`.
 *
 * Examples:
 *   <app-back-button></app-back-button>                                  <!-- "Back" -->
 *   <app-back-button label="Back to projects"
 *                    fallbackLink="/home/project-list"></app-back-button>
 *   <app-back-button variant="icon"
 *                    fallbackLink="/home/project-list"
 *                    matTooltip="Back"></app-back-button>
 *   <app-back-button [link]="['/home/view-project', projectId]"
 *                    label="Back to project"></app-back-button>
 *
 * Listen to (clicked) if you need to confirm before navigating; emitting
 * happens *before* navigation so you can also `event.preventDefault()`-style
 * cancel by setting `[disabled]="true"` from the parent in the same tick if
 * needed.
 */
@Component({
  selector: 'app-back-button',
  templateUrl: './back-button.component.html',
  styleUrls: ['./back-button.component.css'],
})
export class BackButtonComponent {
  /** Text shown next to the icon (ignored when variant === 'icon'). */
  @Input() label = 'Back';

  /** Where to go when there's no in-app history. Accepts string or RouterLink array. */
  @Input() fallbackLink: string | any[] | null = null;

  /** Force a destination (router link). When set, history is ignored. */
  @Input() link: string | any[] | null = null;

  /** Visual variant. `icon` renders an icon-only round button. */
  @Input() variant: BackButtonVariant = 'outline';

  /** Size. `sm` is for compact toolbars; `md` for page headers. */
  @Input() size: BackButtonSize = 'md';

  /** Disable the button (no click, dimmed). */
  @Input() disabled = false;

  /** Tooltip — also used as `aria-label` when variant is 'icon'. */
  @Input() tooltip: string | null = null;

  /** Emitted *before* navigation so parents can short-circuit if needed. */
  @Output() clicked = new EventEmitter<MouseEvent>();

  constructor(
    private location: Location,
    private router: Router,
    private nav: NavigationHistoryService
  ) {}

  get classes(): string {
    const base =
      'inline-flex items-center gap-2 font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed';

    const sizing =
      this.variant === 'icon'
        ? this.size === 'sm'
          ? 'p-1.5'
          : 'p-2'
        : this.size === 'sm'
        ? 'px-3 py-1.5 text-xs'
        : 'px-4 py-2 text-sm';

    const variantClasses = {
      outline:
        'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm',
      ghost: 'text-gray-700 hover:bg-gray-100',
      icon:
        'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-full shadow-sm',
    }[this.variant];

    return `${base} ${variantClasses} ${sizing}`;
  }

  get iconSizeClass(): string {
    if (this.variant === 'icon') {
      return this.size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
    }
    return this.size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  }

  get a11yLabel(): string {
    if (this.tooltip) return this.tooltip;
    if (this.variant === 'icon') return this.label || 'Back';
    return this.label;
  }

  onClick(event: MouseEvent): void {
    if (this.disabled) return;
    this.clicked.emit(event);

    // 1. Forced destination
    if (this.link) {
      if (Array.isArray(this.link)) {
        void this.router.navigate(this.link);
      } else {
        void this.router.navigateByUrl(this.link);
      }
      return;
    }

    // 2. Real in-app history
    if (this.nav.hasInAppHistory()) {
      this.location.back();
      return;
    }

    // 3. Fallback
    if (this.fallbackLink) {
      if (Array.isArray(this.fallbackLink)) {
        void this.router.navigate(this.fallbackLink);
      } else {
        void this.router.navigateByUrl(this.fallbackLink);
      }
      return;
    }

    // 4. Last resort
    void this.router.navigateByUrl('/');
  }
}
